<template>
  <div class="my-account__login-page-wrap account-creation">
    <!-- Page 1, enter account info -->
    <section v-if="currentPage === 1" class="my-account__login-form-wrap account-creation">
      <div class="gds-display-2 gds-space-stack-l">Create Account</div>
      <div class="gds-font-size-l gds-space-stack-m">
        Already have an account?
        <a href="/account">Log In</a>
      </div>
      <form data-vv-scope="page1" class="global-panel__form" @submit.prevent>
        <fieldset class="gds-fieldset gds-space-stack-s">
          <label class="gds-input-field">
            <span class="gds-input-field__label">First Name *</span>
            <input
              v-model="firstName"
              v-validate="'required|max:22'"
              maxlength="22"
              name="first name"
              key="first name"
              class="gds-input-field__input"
              type="text"
              autocomplete="given-name"
              required
              aria-required="true"
            />
          </label>
          <div v-if="errors.first('first name', 'page1')" class="validation-error">{{ errors.first("first name", "page1")}}</div>
        </fieldset>
        <fieldset class="gds-fieldset gds-space-stack-s">
          <label class="gds-input-field">
            <span class="gds-input-field__label">Last Name *</span>
            <input
              v-model="lastName"
              v-validate="'required|max:22'"
              maxlength="22"
              name="last name"
              key="last name"
              class="gds-input-field__input"
              type="text"
              autocomplete="family-name"
              required
              aria-required="true"
            />
          </label>
          <div v-if="errors.first('last name', 'page1')" class="validation-error">{{ errors.first("last name", "page1")}}</div>
        </fieldset>
        <fieldset class="gds-fieldset gds-space-stack-s">
          <label class="gds-input-field">
            <span class="gds-input-field__label">Email Address *</span>
            <input
              v-model="email"
              v-validate="'required|email'"
              name="email address"
              key="email address"
              class="gds-input-field__input"
              type="email"
              spellcheck="false"
              autocapitalize="none"
              autocomplete="email"
              required
              aria-required="true"
            />
          </label>
          <div v-if="errors.first('email address', 'page1')" class="validation-error">{{ errors.first("email address", "page1")}}</div>
        </fieldset>
        <fieldset class="gds-fieldset gds-space-stack-s">
          <label class="gds-input-field">
            <span class="gds-input-field__label">Your GMP Account Number *</span>
            <input
              v-model="accountNumber"
              v-validate="'required|numeric|length:10,11'"
              maxlength="11"
              name="account number"
              key="account number"
              class="gds-input-field__input"
              type="text"
              required
              aria-required="true"
            />
          </label>
          <div v-if="errors.first('account number', 'page1')" class="validation-error">{{ errors.first("account number", "page1")}}</div>
        </fieldset>
        <fieldset class="gds-fieldset gds-space-stack-s">
          <label class="gds-input-field">
            <span class="gds-input-field__label">Account Nickname (Optional)</span>
            <input v-model="accountNickname" v-validate="'max:30'" maxlength="30" name="account nickname" key="account nickname" class="gds-input-field__input" type="text" />
          </label>
          <div v-if="errors.first('account nickname', 'page1')" class="validation-error">{{ errors.first("account nickname", "page1")}}</div>
        </fieldset>
        <fieldset class="gds-fieldset gds-space-stack-s">
          <label class="gds-input-field">
            <span class="gds-input-field__label">Phone (Last 4 Digits) *</span>
            <input
              v-model="phone"
              v-validate="'required|length:4'"
              maxlength="4"
              name="phone number"
              key="phone number"
              class="gds-input-field__input"
              type="tel"
              required
              aria-required="true"
            />
            <div v-if="errors.first('phone number', 'page1')" class="validation-error">{{ errors.first("phone number", "page1")}}</div>
          </label>
        </fieldset>

        <div v-if="errorVerify" class="form-message-box form-message-box--full-width gds-space-stack-l">{{errorVerify}}</div>
        <div class="gds-flex-container gds-space-stack-m">
          <a href="/" class="gds-button gds-secondary gds-space-inline-m">Cancel</a>
          <button @click="VerifyPage1()" :disabled="pendingVerify" :class="{ 'gds-loading': pendingVerify }" type="button" class="gds-button">
            <span class="gds-button__text">Next</span>
          </button>
        </div>
      </form>
    </section>
    <!-- Page 2, create username / password -->
    <section v-if="currentPage === 2" class="my-account__login-form-wrap account-creation">
      <div class="gds-display-2 gds-space-stack-l">Create Username and Password</div>

      <form data-vv-scope="page2" class="global-panel__form" @submit.prevent>
        <fieldset class="gds-fieldset gds-space-stack-s">
          <label class="gds-input-field">
            <span class="gds-input-field__label">Username *</span>
            <input v-model="username" v-validate="'required'" name="username" key="username" class="gds-input-field__input" type="text" required aria-required="true" />
          </label>
          <div v-if="errors.first('username', 'page2')" class="validation-error">{{ errors.first("username", "page2")}}</div>
        </fieldset>
        <fieldset class="gds-fieldset gds-space-stack-s">
          <label class="gds-input-field">
            <span class="gds-input-field__label">Password *</span>
            <widget-password-input
              ref="password"
              v-model="password1"
              v-validate.initial.continues="'required|length:7,30|hasupperandlower|hasnumber'"
              data-vv-validate-on="input"
              name="password"
              key="password"
            />
          </label>
          <!-- begin password requirements -->
          <div class="create-password__requirements">
            <div v-if="!errors.firstByRule('password', 'length', 'page2')" class="create-password__requirements-single">
              <svg class="my-account__green-check-icon gds-icon">
                <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-check" />
              </svg>
              At least 7 characters
            </div>
            <div v-if="errors.firstByRule('password', 'length', 'page2')" class="create-password__requirements-single gds-text-grey">
              <span class="bullet-point">&bull;</span>At least 7 characters
            </div>

            <div v-if="!errors.firstByRule('password', 'hasupperandlower', 'page2')" class="create-password__requirements-single">
              <svg class="my-account__green-check-icon gds-icon">
                <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-check" />
              </svg>
              One lowercase and one uppercase letter
            </div>
            <div v-if="errors.firstByRule('password', 'hasupperandlower', 'page2')" class="create-password__requirements-single gds-text-grey">
              <span class="bullet-point">&bull;</span>One lowercase and one uppercase letter
            </div>

            <div v-if="!errors.firstByRule('password', 'hasnumber', 'page2')" class="create-password__requirements-single">
              <svg class="my-account__green-check-icon gds-icon">
                <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-check" />
              </svg>
              One number
            </div>
            <div v-if="errors.firstByRule('password', 'hasnumber', 'page2')" class="create-password__requirements-single gds-text-grey">
              <span class="bullet-point">&bull;</span>One number
            </div>
          </div>
          <!--/end password requirements -->
        </fieldset>
        <fieldset class="gds-fieldset gds-space-stack-s">
          <label class="gds-input-field">
            <span class="gds-input-field__label">Confirm Password *</span>
            <widget-password-input
              v-model="password2"
              v-validate="'required|confirmed:password'"
              data-vv-validate-on="input"
              name="password confirmation"
              key="password confirmation"
            />
          </label>
          <div v-if="errors.first('password confirmation', 'page2')" class="validation-error">{{ errors.first("password confirmation", "page2")}}</div>
        </fieldset>

        <div class="gds-space-stack-l">
          <div v-if="errorSubmit" class="gds-helper-text-error">{{errorSubmit}}</div>
        </div>

        <div class="gds-flex-container gds-space-stack-m">
          <a href="/" class="gds-button gds-secondary gds-space-inline-m">Cancel</a>
          <button @click="Submit()" :disabled="pendingSubmit" :class="{ 'gds-loading': pendingSubmit }" type="button" class="gds-button">
            <span class="gds-button__text">Register</span>
          </button>
        </div>
      </form>
    </section>
    <section v-if="currentPage === 3" class="my-account__login-form-wrap account-creation-success-wrap account-creation">
      <div class="bill-pay-success__intro-image">
        <img class="my-account__payment-plan-icon" src="/wp-content/themes/gmptwentynineteen/assets/images/creation-success.svg" alt="account successfully created" />
      </div>

      <h2 class="gds-display-2 gds-align--text-center gds-space-stack-ml">Welcome {{this.username}}</h2>
      <div class="gds-font-size-l gds-space-stack-l gds-align--text-center">
        Your account was successfully registered.
        <br />Log in to see your usage, pay bills, and manage account settings.
      </div>

      <div class="gds-space-stack-m global-panel__form">
        <a href="/account" class="gds-button gds-align--block-center">Log In</a>
      </div>
    </section>
  </div>
</template>

<script>
import WidgetPasswordInput from "../WidgetPasswordInput";
import GMPAPI from "../../services/gmpapi";
import { GMPPhoneNumber } from "../../environment";
import { DumpError } from "../../utilities";

export default {
  name: "PageCreateAccount",
  components: {
    WidgetPasswordInput,
  },
  data() {
    return {
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      accountNumber: undefined,
      accountNickname: undefined,
      phone: undefined,

      username: undefined,
      password1: undefined,
      password2: undefined,

      currentPage: 1,

      errorVerify: undefined,
      pendingVerify: false,

      errorSubmit: undefined,
      pendingSubmit: false,

      GMPPhoneNumber,
    };
  },
  computed: {
  },
  async mounted() {
  },
  methods: {
    async VerifyPage1() {
      if (await this.$validator.validateAll("page1")) {
        try {
          this.pendingVerify = true;
          this.errorVerify = undefined;
          await GMPAPI.VerifyAccountPhone(this.accountNumber, this.phone);
          this.currentPage = 2;
        } catch (err) {
          if (err.code === 40) {
            this.errorVerify = "The account number and phone number combination could not be verified. Try again or call " + this.GMPPhoneNumber + ".";
          } else {
            this.errorVerify = "There was an error verifying your account number. Try again or call " + this.GMPPhoneNumber + ".";
          }
        } finally {
          this.pendingVerify = false;
        }
      }
    },
    async Submit() {
      if (await this.$validator.validateAll("page2")) {
        this.pendingSubmit = true;
        this.errorSubmit = undefined;
        try {
          const body = {
            email: this.email,
            username: this.username,
            password: this.password1,
            givenName: this.firstName,
            surname: this.lastName,
            energyAccounts: [
              {
                accountNumber: this.accountNumber,
                nickname: this.accountNickname,
                isPrimary: true,
                accountVerifyValue: this.phone,
                accountVerifyType: "phone",
              },
            ],
          };
          await GMPAPI.CreateUser(body);
          this.currentPage = 3;
        } catch (err) {
          if (err && err.response && err.response.data && err.response.data.errorCode === "USERNAME_TAKEN_MESSAGE") {
            this.errorSubmit = "That username is already taken. Please choose another.";
          } else {
            DumpError("Create account error", err);
            this.errorSubmit = "There was an error creating your account. Try again or call " + this.GMPPhoneNumber + ".";
          }
        } finally {
          this.pendingSubmit = false;
        }
      }
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>